import { AxiosResponse } from 'axios'
import { useCallback, useEffect, useReducer } from 'react'
import { ErrorResponseData } from '../../components/errors/types/errorTypes'
import { request } from '../../utils/request'
import { useIfMounted } from '../useIfMounted'
import {
  Action,
  ActionType,
  FetchHookProps,
  FetchHookReturnType,
  FetchState,
  StatusType
} from './types'

export const useFetch = <T, S = void>({
  query,
  immediate = true
}: FetchHookProps): FetchHookReturnType<T, S> => {
  const ifMounted = useIfMounted()

  const initialState: FetchState<T> = {
    status: StatusType.INIT
  }

  const fetchReducer = (
    fetchState: FetchState<T>,
    action: Action<T>
  ): FetchState<T> => {
    switch (action.type) {
      case ActionType.REQUEST:
        return { ...initialState, status: StatusType.FETCHING }
      case ActionType.SUCCESS:
        return {
          ...initialState,
          status: StatusType.FETCHED,
          data: action.payload
        }
      case ActionType.FAILURE:
        return {
          ...initialState,
          status: StatusType.ERROR,
          error: action.payload
        }
      default:
        return fetchState
    }
  }

  const [fetchState, dispatch] = useReducer(fetchReducer, initialState)

  const execute = useCallback(
    async (variables?: S) => {
      dispatch({ type: ActionType.REQUEST })

      try {
        const response = await request.post('', {
          query: query,
          variables: variables
        })
        ifMounted(() => {
          const errors = (response as AxiosResponse<ErrorResponseData>).data
            .errors
          // handle GraphQL errors
          if (errors) {
            dispatch({ type: ActionType.FAILURE, payload: errors })
          } else {
            dispatch({
              type: ActionType.SUCCESS,
              payload: response?.data?.data
            })
          }
        })
      } catch (error) {
        ifMounted(() => dispatch({ type: ActionType.FAILURE, payload: error }))
      }
    },
    [query, ifMounted]
  )

  useEffect(() => {
    if (immediate) {
      execute()
    }
  }, [execute, immediate])

  return { fetchState, execute }
}
